import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { IoService } from '../../../services/io/io.service';
import { LocationService } from '../../../services/location/location.service';
import { AppComponent } from '../../../app.component';
import { LoadingComponent } from '../../shared/loading/loading.component';
import { DialogOption } from '../../shared/ui/dialog/dialog-options';

const PAPER_WIDTH = 35;

// Full-screen editor for server-rendered register printouts (Template documents
// of type 'register-print'), opened from the Register Print Templates
// configuration report (dialogKey: 'register-print-editor'). Server
// counterpart: /registerPrint (gated by RegisterPrint.manage).
//
// Unlike the receipt editor, the preview is rendered BY THE SERVER against a
// real register and period — the same code path the register's print action
// uses — so what you see is what prints. A Z-Tape previews as an X-Tape
// numbered as the next Z; previews never close a period.
@Component({
  selector: 'app-register-print-editor',
  templateUrl: './register-print-editor.component.html',
  styleUrls: ['./register-print-editor.component.scss']
})
export class RegisterPrintEditorComponent implements OnInit, OnDestroy {

  templateId: string = null;
  isNew = false;
  template: any = null;
  saving = false;

  // Built-ins a new template can start from (and a saved one can reset to).
  builtIns: any[] = [];
  startFromKey: string = null;

  sources: any[] = [];
  scopes: string[] = [];
  reports: any[] = [];

  registers: any[] = [];
  previewLocationId: string = null;
  previewTerminalId: string = null;
  previewScope = '';
  previewLines: Array<{ text: string; tooLong: boolean }> = [];
  previewError: string = null;
  previewLoading = false;
  previewScopeLabel = '';
  ruler = '';

  menuActions: any[] = [];
  binding: { [actionId: string]: boolean } = {};

  showReference = false;

  readonly scopeLabels: { [key: string]: string } = {
    businessDay: 'Business day (since 4 AM)',
    today: 'Today (calendar day)',
    yesterday: 'Yesterday',
    sinceLastZ: 'Since the last Z-Tape',
    last7Days: 'Last 7 days',
    last30Days: 'Last 30 days',
    monthToDate: 'Month to date',
  };

  readonly referenceCommon = [
    { path: 'location', note: 'name, address.streetAddress / addressLocality / addressRegion / postalCode, telephone, website, email' },
    { path: 'client', note: 'name, nameExtended, settings' },
    { path: 'terminal', note: 'name, key — the register printing' },
    { path: 'user', note: 'name, firstName, lastName — who pressed the button' },
    { path: 'scope', note: 'key, label, start, end — the date window (report sources)' },
    { path: 'printedAt', note: 'the moment of printing' },
  ];

  readonly referenceBySource: { [key: string]: Array<{ path: string; note: string }> } = {
    zTape: [
      { path: 'tape', note: 'kind (Z/X), zNumber, start, end, userName, terminal.name, lastZ { zNumber, end }' },
      { path: 'tape.sales', note: 'gross, returns, discounts (negative), net, tax, tips, rounding, giftSold, collected (cents)' },
      { path: 'tape.tenders[]', note: 'name, payments, refunds, net, count — one per tender type' },
      { path: 'tape.tendersTotal', note: 'cents' },
      { path: 'tape.counts', note: 'sales, returns, voids, declined, averageSale' },
      { path: 'tape.cash', note: 'sales, refunds (negative), lines[] { name, amount, count } (paid outs, drops…), adjustments, noSaleCount, startBalance, endBalance' },
      { path: 'tape.employees[]', note: 'name, sales, returns, net' },
      { path: 'tape.categories[]', note: 'name, net' },
    ],
    report: [
      { path: 'report', note: 'name, key, fields[]' },
      { path: 'columns[]', note: 'the report\'s active fields: path, name, type' },
      { path: 'records[]', note: 'one object per report row, keyed by field path' },
      { path: 'totals', note: 'the totals row keyed by field path (count, and each totalled field)' },
      { path: 'fmt(record, field)', note: 'formats a field the way the report grid does (money, number, date…)' },
    ],
  };

  readonly referenceHelpers = [
    { sig: 'h.header()', note: 'the location header block (client name, address, phone)' },
    { sig: 'h.row(label, value)', note: 'label left, value at the margin; the label is cut so the value never wraps' },
    { sig: 'h.heading(text)', note: 'a rule, then the text in capitals' },
    { sig: 'h.rule()', note: 'a full-width line of dashes' },
    { sig: 'h.line(left, right)', note: 'left text with right text at the margin; long left text wraps' },
    { sig: 'h.center(text) / h.left(text) / h.right(text)', note: 'align within the paper width' },
    { sig: 'h.repeat(ch)', note: 'a full-width rule of one character' },
    { sig: 'h.money(cents) / h.amount(cents)', note: '$12.34 / 12.34 — a negative prints as -$12.34' },
    { sig: 'h.number(v, decimals)', note: '1,234 or 1,234.50' },
    { sig: 'h.dateTime(v) / h.date(v) / h.time(v)', note: 'in the location\'s time zone: 10/08/2026 7:00 PM' },
    { sig: 'h.phone(digits)', note: '(260) 555-1234' },
    { sig: 'h.upper(v) / h.lower(v) / h.truncate(v, n)', note: 'text helpers' },
    { sig: 'h.padLeft(v, n) / h.padRight(v, n)', note: 'fixed-width columns' },
    { sig: 'h.wrap(text)', note: 'word-wrap to the paper width; returns lines' },
  ];

  private previewTimer: any = null;
  private previewRequest = 0;

  public static showModal(templateId: string | null = null): MatDialogRef<RegisterPrintEditorComponent> {
    return AppComponent.dialog.open(RegisterPrintEditorComponent, {
      width: '100%',
      height: '100%',
      maxWidth: '100%',
      panelClass: 'full-screen-dialog',
      hasBackdrop: true,
      disableClose: false,
      data: {
        templateId,
      },
    });
  }

  constructor(
    private ioService: IoService,
    private locationService: LocationService,
    private dialogRef: MatDialogRef<RegisterPrintEditorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.templateId = data ? data.templateId : null;
  }

  ngOnInit(): void {
    this.isNew = !this.templateId;
    this.ruler = this.buildRuler(PAPER_WIDTH);
    // Defer past the dialog's first change detection (showLoading flips a shell binding).
    Promise.resolve().then(() => this.load());
  }

  ngOnDestroy(): void {
    if (this.previewTimer) {
      clearTimeout(this.previewTimer);
    }
  }

  load = async () => {
    LoadingComponent.showLoading();
    try {
      const sourceInfo: any = await this.ioService.post('/registerPrint/getSources');
      this.sources = sourceInfo.sources || [];
      this.scopes = sourceInfo.scopes || [];
      this.reports = sourceInfo.reports || [];

      const all: any[] = await this.ioService.post('/registerPrint/getTemplates') as any[];
      this.builtIns = all.filter(t => t.builtIn);
      // Built-ins a tenant already overrides still serve as starting points.
      for (const key of ['z-tape', 'x-tape', 'report-summary']) {
        if (!this.builtIns.some(b => b.key === key)) {
          this.builtIns.push({ key, name: key, builtIn: true });
        }
      }

      if (this.isNew) {
        this.startFromKey = this.builtIns.length ? this.builtIns[0].key : null;
        const builtIn: any = this.startFromKey
          ? await this.ioService.post('/registerPrint/getBuiltInTemplate', { key: this.startFromKey })
          : { ejs: '', source: { key: 'xTape' } };
        this.template = this.normalize({ ...builtIn, name: '', key: '', description: '' });
      } else {
        const template = await this.ioService.post('/registerPrint/getTemplate', { templateId: this.templateId });
        this.template = this.normalize(template);
      }

      this.registers = await this.ioService.post('/registerPrint/getRegisters') as any[];
      const active = this.locationService.getActiveLocation();
      const activeId = active && active._id ? String(active._id) : null;
      const withRegisters = this.registers.filter(l => l.terminals && l.terminals.length > 0);
      const preferred = withRegisters.find(l => String(l._id) === activeId) || withRegisters[0] || this.registers[0] || null;
      this.previewLocationId = preferred ? String(preferred._id) : null;
      this.onPreviewLocationChanged(false);

      if (!this.isNew) {
        await this.loadMenuActions();
      }
      await this.renderPreview();
    } catch (err) {
      AppComponent.showSnack(err.error && err.error.msg ? err.error.msg : 'Unable to load the print template.', 'OK', 5000);
      this.dialogRef.close(null);
    }
    LoadingComponent.hideLoading();
  }

  private normalize(doc: any): any {
    const template = { ...doc };
    template.name = template.name || '';
    template.key = template.key || '';
    template.description = template.description || '';
    template.ejs = typeof template.ejs === 'string' ? template.ejs : '';
    template.source = template.source && template.source.key ? { ...template.source } : { key: 'xTape' };
    template.source.reportKey = template.source.reportKey || '';
    template.scope = template.scope || '';
    template.logo = template.logo !== false;
    template.cutReceipt = template.cutReceipt !== false;
    template.openCashDrawer = template.openCashDrawer === true;
    template.successMessage = template.successMessage || '';
    template.active = template.active !== false;
    delete template.builtIn;
    return template;
  }

  private buildRuler(width: number): string {
    let ruler = '';
    for (let i = 1; i <= width; i++) {
      ruler += i % 10 === 0 ? String((i / 10) % 10) : (i % 5 === 0 ? '+' : '·');
    }
    return ruler;
  }

  // ---- source / scope ------------------------------------------------------

  get source(): any {
    return this.sources.find(s => s.key === this.template.source.key) || null;
  }

  get sourceUsesScope(): boolean {
    return !!(this.source && this.source.usesScope);
  }

  get overridesBuiltIn(): boolean {
    return !!this.template && this.builtIns.some(b => b.key === this.template.key);
  }

  scopeLabel(key: string): string {
    return this.scopeLabels[key] || key;
  }

  async onStartFromChanged() {
    if (!this.startFromKey) {
      return;
    }
    try {
      const builtIn: any = await this.ioService.post('/registerPrint/getBuiltInTemplate', { key: this.startFromKey });
      const keep = { name: this.template.name, key: this.template.key, description: this.template.description };
      this.template = this.normalize({ ...builtIn, ...keep });
      this.templateChanged();
    } catch (err) {
      AppComponent.showSnack('Unable to load the built-in template.', 'OK', 5000);
    }
  }

  onSourceChanged() {
    if (this.template.source.key !== 'report') {
      this.template.source = { key: this.template.source.key };
      this.template.source.reportKey = '';
    }
    this.templateChanged();
  }

  // ---- preview -------------------------------------------------------------

  onPreviewLocationChanged(render = true) {
    const location = this.registers.find(l => String(l._id) === String(this.previewLocationId));
    const terminals = (location && location.terminals) || [];
    this.previewTerminalId = terminals.length ? String(terminals[0]._id) : null;
    if (render) {
      this.renderPreview();
    }
  }

  get previewTerminals(): any[] {
    const location = this.registers.find(l => String(l._id) === String(this.previewLocationId));
    return (location && location.terminals) || [];
  }

  templateChanged() {
    if (this.previewTimer) {
      clearTimeout(this.previewTimer);
    }
    this.previewTimer = setTimeout(() => {
      this.previewTimer = null;
      this.renderPreview();
    }, 600);
  }

  async renderPreview() {
    if (!this.template) {
      return;
    }
    if (!this.previewLocationId) {
      this.previewError = 'No location to preview with.';
      this.previewLines = [];
      return;
    }
    if (this.template.source.key === 'report' && !this.template.source.reportKey) {
      this.previewError = 'Choose the report this template prints.';
      this.previewLines = [];
      return;
    }
    const request = ++this.previewRequest;
    this.previewLoading = true;
    try {
      const payload: any = {
        ejs: this.template.ejs,
        source: this.template.source,
        locationId: this.previewLocationId,
        terminalId: this.previewTerminalId || undefined,
      };
      const scope = this.previewScope || this.template.scope || '';
      if (scope) {
        payload.scope = scope;
      }
      const result: any = await this.ioService.post('/registerPrint/preview', payload);
      if (request !== this.previewRequest) {
        return;
      }
      const width = result.width || PAPER_WIDTH;
      this.previewError = null;
      this.previewScopeLabel = result.scope && result.scope.label ? result.scope.label : '';
      this.previewLines = String(result.text || '').replace(/\n$/, '').split('\n')
        .map(line => ({ text: line, tooLong: line.length > width }));
    } catch (err) {
      if (request !== this.previewRequest) {
        return;
      }
      this.previewLines = [];
      this.previewError = err.error && err.error.msg ? err.error.msg : 'Unable to render the preview.';
    }
    this.previewLoading = false;
  }

  // ---- editor --------------------------------------------------------------

  // Tab inserts two spaces instead of leaving the textarea.
  onEditorKeydown(event: KeyboardEvent) {
    if (event.key !== 'Tab') {
      return;
    }
    event.preventDefault();
    const area = event.target as HTMLTextAreaElement;
    const start = area.selectionStart;
    const end = area.selectionEnd;
    const value = area.value;
    area.value = value.substring(0, start) + '  ' + value.substring(end);
    area.selectionStart = area.selectionEnd = start + 2;
    this.template.ejs = area.value;
    this.templateChanged();
  }

  resetToBuiltIn() {
    const key = this.overridesBuiltIn ? this.template.key : this.startFromKey;
    if (!key) {
      return;
    }
    const confirmRef = AppComponent.showMessage(
      'Reset Template',
      `Replace the template text with the built-in "${key}" layout? Unsaved edits will be lost.`,
      ['Reset', 'Cancel'],
      false
    );
    confirmRef.afterClosed().subscribe(async (result: DialogOption) => {
      if (!result || result.value !== 'Reset') {
        return;
      }
      try {
        const builtIn: any = await this.ioService.post('/registerPrint/getBuiltInTemplate', { key });
        this.template.ejs = builtIn.ejs;
        this.templateChanged();
      } catch (err) {
        AppComponent.showSnack('Unable to load the built-in template.', 'OK', 5000);
      }
    });
  }

  // ---- menu actions --------------------------------------------------------

  async loadMenuActions() {
    try {
      const actions: any[] = await this.ioService.post('/registerPrint/getMenuActions') as any[];
      // Only actions a printout can hang on: no transaction type and no form.
      this.menuActions = actions.filter(a => !a.transactionType && !a.formKey);
    } catch (err) {
      this.menuActions = [];
    }
  }

  printsThisTemplate(action: any): boolean {
    return !!this.template.key && action.printKey === this.template.key;
  }

  async toggleAction(action: any, use: boolean) {
    if (this.isNew || !this.template.key) {
      return;
    }
    await this.setBinding(action, use ? this.template.key : null, use ? (action.printScope || null) : null);
  }

  async changeActionScope(action: any, scope: string) {
    await this.setBinding(action, this.template.key, scope || null);
  }

  private async setBinding(action: any, printKey: string | null, printScope: string | null) {
    const actionId = String(action._id);
    this.binding[actionId] = true;
    try {
      const result: any = await this.ioService.post('/registerPrint/setMenuActionPrint', {
        menuActionId: actionId,
        printKey,
        printScope,
      });
      action.printKey = result.printKey || null;
      action.printScope = result.printScope || null;
      AppComponent.showSnack(printKey
        ? `"${action.name}" now prints "${this.template.name || this.template.key}".`
        : `"${action.name}" no longer prints this template.`, 'OK', 3000);
    } catch (err) {
      AppComponent.showSnack(err.error && err.error.msg ? err.error.msg : 'Unable to update the menu action.', 'OK', 5000);
    }
    this.binding[actionId] = false;
  }

  // ---- save / delete -------------------------------------------------------

  private textOrNull(value: any): string | null {
    const text = value === undefined || value === null ? '' : String(value).trim();
    return text.length > 0 ? text : null;
  }

  buildPayload() {
    const source: any = { key: this.template.source.key };
    if (source.key === 'report') {
      source.reportKey = this.textOrNull(this.template.source.reportKey) || '';
    }
    const payload: any = {
      name: this.textOrNull(this.template.name),
      description: this.textOrNull(this.template.description),
      ejs: this.template.ejs,
      source,
      scope: this.sourceUsesScope ? (this.textOrNull(this.template.scope) || null) : null,
      logo: this.template.logo !== false,
      cutReceipt: this.template.cutReceipt !== false,
      openCashDrawer: this.template.openCashDrawer === true,
      successMessage: this.textOrNull(this.template.successMessage),
      active: this.template.active !== false,
    };
    const key = this.textOrNull(this.template.key);
    if (key) {
      payload.key = key;
    }
    if (!payload.name) {
      delete payload.name;
    }
    if (this.isNew) {
      // create() ignores nulls; update() treats null as "clear the field".
      for (const field of Object.keys(payload)) {
        if (payload[field] === null) {
          delete payload[field];
        }
      }
    }
    return payload;
  }

  async save() {
    if (!this.textOrNull(this.template.name)) {
      AppComponent.showSnack('Template name is required.', 'OK', 5000);
      return;
    }
    if (this.template.source.key === 'report' && !this.textOrNull(this.template.source.reportKey)) {
      AppComponent.showSnack('Choose the report this template prints.', 'OK', 5000);
      return;
    }
    if (this.saving) {
      return;
    }
    this.saving = true;
    LoadingComponent.showLoading();
    try {
      if (this.isNew) {
        const created: any = await this.ioService.post('/registerPrint/createTemplate', {
          template: this.buildPayload(),
        });
        AppComponent.showSnack('Print template created. Bind it to a register menu action to print it.', 'OK', 5000);
        this.dialogRef.close({ saved: true, templateId: created && created._id });
      } else {
        await this.ioService.post('/registerPrint/updateTemplate', {
          templateId: this.templateId,
          changes: this.buildPayload(),
        });
        AppComponent.showSnack('Print template saved. Registers use it on their next print.', 'OK', 4000);
        this.dialogRef.close({ saved: true });
      }
    } catch (err) {
      AppComponent.showSnack(err.error && err.error.msg ? err.error.msg : 'Unable to save the print template.', 'OK', 8000);
    }
    this.saving = false;
    LoadingComponent.hideLoading();
  }

  deactivate() {
    const fallback = this.overridesBuiltIn
      ? 'Registers will print the built-in "' + this.template.key + '" layout instead.'
      : 'Menu actions bound to it will stop printing until bound to another template.';
    const confirmRef = AppComponent.showMessage(
      'Deactivate Print Template',
      `Deactivate "${this.template.name}"? ${fallback}`,
      ['Confirm', 'Cancel'],
      false
    );
    confirmRef.afterClosed().subscribe(async (result: DialogOption) => {
      if (!result || result.value !== 'Confirm') {
        return;
      }
      try {
        await this.ioService.post('/registerPrint/deleteTemplate', { templateId: this.templateId });
        AppComponent.showSnack('Print template deactivated.', 'OK', 3000);
        this.dialogRef.close({ saved: true });
      } catch (err) {
        AppComponent.showSnack(err.error && err.error.msg ? err.error.msg : 'Unable to deactivate the print template.', 'OK', 5000);
      }
    });
  }

  close() {
    this.dialogRef.close(null);
  }
}
